import React, { useEffect, useCallback } from "react";
import clsx from "clsx";
import { connect } from "react-redux";

import { Skeleton } from "components";
import BankBalanceTable from "components/tables/bankBalanceTable";
import FinancialTable from "components/tables/financialsTable";
import { financialActions, uiActions } from "store";

import styles from "./financials.module.scss";

const UwFinancials = ({
	applicationId,
	loading,
	financials,
	bankBalances,

	getFinancials,
	addFinancial,
	editFinancial,
	deleteFinancial,

	getBankBalances,
	addBankBalance,
	editBankBalance,
	deleteBankBalance,
	showModal,
	isEditable,
}) => {
	useEffect(() => {
		getFinancials(applicationId);
		getBankBalances(applicationId);
	}, [applicationId, getBankBalances, getFinancials]);

	const handleAddFinancial = async financial => {
		// Form returns id="". API fails to parse it and throws error
		delete financial.id;
		await addFinancial(financial, applicationId);
		getFinancials(applicationId);
	};

	const handleAddBankBalance = async bankBalance => {
		delete bankBalance.id;
		await addBankBalance(bankBalance, applicationId);
		getBankBalances(applicationId);
	};

	const handleEditFinancial = async (index, financial) => {
		await editFinancial({ ...financial, applicationId });
		getFinancials(applicationId);
	};

	const handleEditBankBalance = async (index, bankBalance) => {
		// Let it be
		await editBankBalance({ ...bankBalance, applicationId }, applicationId);
		getBankBalances(applicationId);
	};

	const deleteItemFn = useCallback(
		async item => {
			if (item.type === "financial") {
				await deleteFinancial(item.id);
				getFinancials(applicationId);
			} else if (item.type === "bankBalance") {
				await deleteBankBalance(item.id);
				getBankBalances(applicationId);
			}
		},
		[
			applicationId,
			deleteBankBalance,
			deleteFinancial,
			getBankBalances,
			getFinancials,
		],
	);

	const handleDelete = useCallback(
		item => {
			showModal("CONFIRMATION", {
				title: "Are you sure you want to delete this item? You won't be able to undo.",
				onConfirm: () => {
					return deleteItemFn(item);
				},
			});
		},
		[deleteItemFn, showModal],
	);

	const Financials = (
		<>
			<p className={styles.tableTitle}>Financials</p>
			<FinancialTable
				financials={financials}
				addFinancial={handleAddFinancial}
				editFinancial={handleEditFinancial}
				deleteFinancial={(index, id) =>
					handleDelete({ type: "financial", id })
				}
				isEditable={isEditable}
				showEmptyBlock={!isEditable}
				hideFooter={true}
			/>
			<p className={clsx(styles.tableTitle, styles.bankBalanceHeader)}>
				Bank Balance
			</p>
			<BankBalanceTable
				bankBalances={bankBalances}
				addBankBalance={handleAddBankBalance}
				editBankBalance={handleEditBankBalance}
				deleteBankBalance={(index, id) =>
					handleDelete({ type: "bankBalance", id })
				}
				isEditable={isEditable}
				showEmptyBlock={!isEditable}
				hideFooter={true}
			/>
		</>
	);
	const BlockWithLoader = useCallback(
		({ financials }) =>
			loading ? (
				<Skeleton height="calc(100vh - 250px)" marginTop="30px" />
			) : (
				financials
			),
		[loading],
	);

	return <BlockWithLoader financials={Financials} />;
};

const mapStateToProps = state => ({
	loading:
		state.financial.financialsLoading || state.financial.bankBalanceLoading,
	financials: state.financial.financials,
	bankBalances: state.financial.bankBalances,
});
const mapDispatchToProps = {
	getFinancials: financialActions.getFinancials,
	addFinancial: financialActions.addFinancial,
	editFinancial: financialActions.editFinancial,
	deleteFinancial: financialActions.deleteFinancial,

	getBankBalances: financialActions.getBankBalances,
	addBankBalance: financialActions.addBankBalance,
	editBankBalance: financialActions.editBankBalance,
	deleteBankBalance: financialActions.deleteBankBalance,
	showModal: uiActions.showModal,
};

export default connect(mapStateToProps, mapDispatchToProps)(UwFinancials);
